/* Load once near the start of <head>, without defer/async, before PCFitment. */
(() => {
  'use strict';
  if (window.NAGDVehicle) return;

  const STORAGE_KEY = 'nagd.submittedVehicle.v1';
  const EVENT = 'nagd:vehicle-change';
  const fields = ['year', 'make', 'model', 'submodel', 'doors', 'bodyType'];
  const inputIds = ['ddlyear', 'ddlmake', 'ddlmodel', 'ddlsubmodel', 'ddlbodynumdoor', 'ddlbodytype'];
  const readyTables = new WeakMap();
  let fitmentLoadState = 'idle';
  const editedInputs = new WeakSet();
  const clean = value => String(value ?? '').replace(/\s+/g, ' ').trim();
  const unspecified = value => !clean(value) || /^all$/i.test(clean(value));

  function readPersistent(key) {
    try { return localStorage.getItem(key); }
    catch (_) {
      try { return sessionStorage.getItem(key); } catch (_) { return null; }
    }
  }

  function writePersistent(key, value) {
    try {
      localStorage.setItem(key, value);
      try { sessionStorage.removeItem(key); } catch (_) {}
    } catch (_) {
      try { sessionStorage.setItem(key, value); } catch (_) {}
    }
  }

  function migrateTabData(key) {
    try {
      if (localStorage.getItem(key) === null) {
        const legacy = sessionStorage.getItem(key);
        if (legacy !== null) localStorage.setItem(key, legacy);
      }
      sessionStorage.removeItem(key);
    } catch (_) { /* Keep the tab fallback if persistent storage is blocked. */ }
  }

  function validate(value) {
    if (!value || typeof value !== 'object') return null;
    const vehicle = {};
    for (const field of fields) {
      if (value[field] != null && typeof value[field] !== 'string') return null;
      const text = clean(value[field]);
      if (text.length > 150) return null;
      vehicle[field] = unspecified(text) ? '' : text;
    }
    if (!/^\d{4}$/.test(vehicle.year) || !vehicle.make || !vehicle.model) return null;
    return vehicle;
  }

  let current = null;
  migrateTabData(STORAGE_KEY);
  try {
    const saved = JSON.parse(readPersistent(STORAGE_KEY) || 'null');
    if (saved?.version === 1) current = validate(saved.vehicle);
  } catch (_) { /* Storage can be blocked or contain invalid JSON. */ }

  function getVehicle() {
    return current ? { ...current } : null;
  }

  function publish() {
    window.dispatchEvent(new CustomEvent(EVENT, { detail: { vehicle: getVehicle() } }));
  }

  // For future VIN/garage integration: call ONLY after a verified selection.
  function setVehicle(value) {
    const vehicle = validate(value);
    if (!vehicle) return false;
    current = vehicle;
    try {
      writePersistent(STORAGE_KEY, JSON.stringify({ version: 1, vehicle }));
    } catch (_) { /* Keep the current-page selection if storage is unavailable. */ }
    publish();
    return true;
  }

  function clearVehicle() {
    current = null;
    // Persist a cleared record so an older tab cannot migrate a stale vehicle back.
    writePersistent(STORAGE_KEY, JSON.stringify({ version: 1, vehicle: null }));
    publish();
  }

  function readGarageVehicle(index) {
    if (!/^\d+$/.test(String(index))) return null;
    // Mirror PCFitment's choice of account-backed versus guest garage data.
    const allowed = document.getElementById('HdnIsGarageAllow')?.value;
    const customer = clean(document.getElementById('HdnShopifyCustomerId')?.value);
    let record;
    try {
      if (allowed !== 'false' && customer) {
        const raw = document.getElementById('hdngaragesearchvehicle_' + index)?.value;
        const values = typeof raw === 'string' ? raw.split('&') : [];
        if (values.length !== 12) return null;
        record = {
          year: values[6], make: values[7], model: values[8], submodel: values[9],
          bodyType: values[10], doors: values[11]
        };
      } else {
        const saved = JSON.parse(localStorage.getItem('VehicleGarage_' + index) || 'null');
        if (!saved || typeof saved !== 'object') return null;
        record = {
          year: saved.year, make: saved.make, model: saved.model,
          submodel: saved.submodel, doors: saved.bodynumdoor, bodyType: saved.bodytype
        };
      }
      for (const field of fields) {
        if (record[field] != null && !['string', 'number'].includes(typeof record[field])) return null;
        const value = clean(record[field]);
        record[field] = /^(undefined|null)$/i.test(value) ? '' : value;
      }
      return validate(record);
    } catch (_) { return null; }
  }

  function readSubmittedVehicle() {
    const container = document.getElementById('SearchDropDownContainer');
    if (!container) return null;
    const vehicle = {};
    for (let index = 0; index < fields.length; index++) {
      const input = container.querySelector('#' + inputIds[index]);
      const text = clean(input?.value);
      const selectedId = clean(input?.getAttribute('data-id'));
      const blocked = !input || input.disabled || input.getAttribute('aria-disabled') === 'true' ||
        Boolean(input.closest('.vehicle-field-locked, .vehicle-field-waiting'));
      if (blocked) {
        if (index < 3) return null;
        vehicle[fields[index]] = '';
        continue;
      }
      // Typing is not selection, even if a previous data-id remains on the input.
      if (editedInputs.has(input)) return null;
      if (unspecified(text) || /^all$/i.test(selectedId)) {
        if (index < 3) return null;
        vehicle[fields[index]] = '';
        continue;
      }
      // Reject text that has no provider selection, rather than silently ignore it.
      if (!selectedId) return null;
      const listId = input.getAttribute('list');
      const list = listId ? document.getElementById(listId) : null;
      const options = list ? Array.from(list.querySelectorAll('td')) : [];
      if (options.length && !options.some(option => clean(option.textContent).toLowerCase() === text.toLowerCase())) return null;
      vehicle[fields[index]] = text;
    }
    return validate(vehicle);
  }

  // A completion acknowledgement applies only to this exact table content/page.
  function tableSignature(table) {
    return JSON.stringify(Array.from(table.rows, row =>
      Array.from(row.cells, cell => {
        let text = clean(cell.textContent);
        if (cell.tagName === 'TH') {
          text = text.toLowerCase().replace(/[^a-z]/g, '');
          if (['bodynumdoor', 'bodynumdoors'].includes(text)) text = 'doors';
        }
        return [cell.tagName, cell.colSpan, cell.rowSpan, text];
      })));
  }

  function markFitmentTableReady(table) {
    if (!table || table.id !== 'bc-applications' || !table.isConnected) return false;
    readyTables.set(table, { path: location.pathname, signature: tableSignature(table) });
    fitmentLoadState = 'ready';
    window.dispatchEvent(new CustomEvent('nagd:fitment-table-change'));
    return true;
  }

  function markFitmentTablePending(table) {
    fitmentLoadState = 'loading';
    if (table) readyTables.delete(table);
    window.dispatchEvent(new CustomEvent('nagd:fitment-table-change'));
  }

  function isFitmentTableReady(table) {
    if (!table?.isConnected) return false;
    const saved = readyTables.get(table);
    if (!saved) return false;
    if (saved.path !== location.pathname || saved.signature !== tableSignature(table)) {
      readyTables.delete(table);
      return false;
    }
    return true;
  }

  // Reuse completed dropdown responses, retaining their exact query scope.
  const OPTION_CACHE_KEY = 'nagd.fitmentOptions.v1';
  const OPTION_LIMIT = 300;
  const OPTION_TTL = 7 * 24 * 60 * 60 * 1000;
  let optionCache = [];
  migrateTabData(OPTION_CACHE_KEY);
  function refreshOptionCache() {
    try {
      const raw = readPersistent(OPTION_CACHE_KEY);
      const saved = JSON.parse(raw || '[]');
      if (Array.isArray(saved)) optionCache = saved.filter(entry =>
        entry && typeof entry.key === 'string' && Number.isFinite(entry.time) &&
        Date.now() >= entry.time && Date.now() - entry.time < OPTION_TTL).slice(-OPTION_LIMIT);
    } catch (_) {}
  }
  refreshOptionCache();

  function optionKey(url) {
    try {
      if (typeof window.baseHandlerurl !== 'string') return null;
      const configured = new URL(window.baseHandlerurl, location.href);
      const parsed = new URL(url, location.href);
      if (configured.protocol !== 'https:' || parsed.origin !== configured.origin || parsed.pathname !== configured.pathname) return null;
      if (!['submodel', 'bodynumdoor', 'bodytype'].includes(parsed.searchParams.get('method'))) return null;
      if (!['pcid', 'year', 'make', 'model'].every(key => parsed.searchParams.get(key))) return null;
      // txt* fields are display labels; preserve all actual filters, including
      // any engine/category restriction, so a narrow list cannot prove a broad one.
      const params = [...parsed.searchParams].filter(([key, value]) => !key.startsWith('txt') && value !== '');
      params.sort((a, b) => a[0].localeCompare(b[0]) || a[1].localeCompare(b[1]));
      return JSON.stringify([parsed.origin, parsed.pathname, params]);
    } catch (_) { return null; }
  }

  function validOptions(data) {
    return Array.isArray(data) && data.length > 0 && data.length <= 250 && data.every(option =>
      option && ['string', 'number'].includes(typeof option.value) && typeof option.text === 'string' &&
      clean(option.value) && clean(option.text));
  }

  function rememberFitmentOptions(url, data) {
    const key = optionKey(url);
    if (!key || !validOptions(data)) return false;
    refreshOptionCache();
    optionCache = optionCache.filter(entry => entry.key !== key);
    optionCache.push({ key, time: Date.now(), data: data.map(option => ({ value: clean(option.value), text: clean(option.text) })) });
    optionCache = optionCache.slice(-OPTION_LIMIT);
    writePersistent(OPTION_CACHE_KEY, JSON.stringify(optionCache));
    return true;
  }

  function getFitmentOptions(url) {
    refreshOptionCache();
    const key = optionKey(url);
    const entry = optionCache.find(item => item.key === key && Date.now() >= item.time && Date.now() - item.time < OPTION_TTL);
    return entry && validOptions(entry.data) ? entry.data.map(option => ({ ...option })) : null;
  }

  // Navigation cancels requests on the departing page. Warm the cache as soon
  // as the results page has restored the submitted vehicle's dropdown IDs.
  function startOptionsPreload() {
    if (!/^\/pages\/search-result\/?$/.test(location.pathname)) return;
    let job = null;
    async function preload(vehicle, context, controller) {
      let requests = 0;
      const deadline = setTimeout(() => controller.abort(), 60000);
      async function options(method, branch) {
        if (controller.signal.aborted) throw new Error('Cancelled');
        const url = new URL(context.endpoint);
        url.search = '';
        const params = { pcid: context.store, method, ...context.ids, ...branch };
        for (const field of ['year', 'make', 'model']) params['txt' + field] = vehicle[field];
        for (const [key, value] of Object.entries(params)) url.searchParams.set(key, value);
        let data = getFitmentOptions(url.href);
        if (!data) {
          if (++requests > 100) throw new Error('Preload limit');
          const response = await fetch(url.href, { signal: controller.signal, credentials: 'omit' });
          if (!response.ok) throw new Error('Options unavailable');
          data = await response.json();
          if (controller.signal.aborted || !rememberFitmentOptions(url.href, data)) throw new Error('Invalid options');
        }
        return data.filter(option => !unspecified(option.value) && !unspecified(option.text) &&
          !/^(undefined|null)$/i.test(clean(option.value)) && !/^(undefined|null)$/i.test(clean(option.text)));
      }
      async function walk(index, branch) {
        if (index === 3) return;
        const [field, method] = [['submodel', 'submodel'], ['doors', 'bodynumdoor'], ['bodyType', 'bodytype']][index];
        const all = await options(method, branch);
        const choices = vehicle[field] ? all.filter(option => clean(option.text).toLowerCase() === vehicle[field].toLowerCase()) : all;
        for (const option of choices) {
          await walk(index + 1, { ...branch, [method]: option.value, ['txt' + method]: option.text });
        }
      }
      try { await walk(0, {}); } catch (_) { /* Product pages can fetch missing branches later. */ }
      finally { clearTimeout(deadline); }
    }
    function check() {
      const vehicle = getVehicle();
      if (!vehicle) { job?.controller.abort(); job = null; return; }
      const vehicleKey = JSON.stringify(vehicle);
      if (job && job.vehicleKey !== vehicleKey) { job.controller.abort(); job = null; }
      try {
        if (typeof window.baseHandlerurl !== 'string') return;
        const endpoint = new URL(window.baseHandlerurl, location.href);
        const store = clean(document.getElementById('pcappid')?.value);
        if (endpoint.protocol !== 'https:' || !store) return;
        const ids = {};
        for (const field of ['year', 'make', 'model']) {
          const input = document.getElementById('ddl' + field);
          const id = clean(input?.getAttribute('data-id'));
          if (!input || unspecified(id) || clean(input.value).toLowerCase() !== vehicle[field].toLowerCase()) return;
          if (editedInputs.has(input) || input.closest('.vehicle-field-locked, .vehicle-field-waiting')) return;
          ids[field] = id;
        }
        const context = { endpoint: endpoint.href, store, ids };
        const key = JSON.stringify([vehicle, context]);
        if (job?.key === key) return;
        job?.controller.abort();
        job = { key, vehicleKey, controller: new AbortController() };
        void preload(vehicle, context, job.controller);
      } catch (_) {}
    }
    let scheduled = false;
    function schedulePreload() {
      if (scheduled) return;
      scheduled = true;
      queueMicrotask(() => { scheduled = false; check(); });
    }
    const observer = new MutationObserver(schedulePreload);
    observer.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['data-id', 'value'] });
    window.addEventListener(EVENT, schedulePreload);
    window.addEventListener('pageshow', schedulePreload);
    window.addEventListener('pagehide', () => { job?.controller.abort(); job = null; });
    schedulePreload();
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', startOptionsPreload, { once: true });
  else startOptionsPreload();

  window.NAGDVehicle = Object.freeze({
    getFitmentLoadState: () => fitmentLoadState,
    getVehicle, setVehicle, clearVehicle,
    getFitmentOptions, rememberFitmentOptions,
    markFitmentTableReady, markFitmentTablePending, isFitmentTableReady
  });

  // PCFitment's getBuyerGuideInfo inserts this XHR's HTML on readyState 4/200.
  // Install before the provider starts its requests; see the non-deferred include.
  const originalOpen = XMLHttpRequest.prototype.open;
  let latestGuideRequest = 0;
  XMLHttpRequest.prototype.open = function(method, url, ...rest) {
    let isGuide = false;
    try {
      const parsed = new URL(String(url), location.href);
      // Read the provider's current configuration at request time, not a saved host.
      const configured = typeof window.baseHandlerurl === 'string' && window.baseHandlerurl.trim()
        ? new URL(window.baseHandlerurl, location.href) : null;
      isGuide = String(method).toUpperCase() === 'GET' &&
        configured !== null && configured.protocol === 'https:' &&
        parsed.origin === configured.origin && parsed.pathname === configured.pathname &&
        parsed.searchParams.get('method') === 'buyerguidedata' &&
        parsed.searchParams.get('pcid') === '18068367' &&
        Boolean(parsed.searchParams.get('PartNumber'));
    } catch (_) {}
    const result = originalOpen.call(this, method, url, ...rest);
    if (String(method).toUpperCase() === 'GET' && optionKey(url)) {
      const optionURL = String(url);
      this.addEventListener('load', () => {
        if (this.status !== 200) return;
        try { rememberFitmentOptions(optionURL, JSON.parse(this.responseText)); } catch (_) {}
      }, { once: true });
    }
    if (!isGuide) return result;
    const requestId = ++latestGuideRequest;
    this.addEventListener('loadend', () => queueMicrotask(() => {
      if (requestId === latestGuideRequest && fitmentLoadState === 'loading') {
        fitmentLoadState = 'unavailable';
        window.dispatchEvent(new CustomEvent('nagd:fitment-table-change'));
      }
    }), { once: true });
    const path = location.pathname;
    markFitmentTablePending(document.getElementById('bc-applications'));
    this.addEventListener('load', () => {
      if (requestId !== latestGuideRequest || location.pathname !== path || this.status !== 200) return;
      try {
        const response = new DOMParser().parseFromString(this.responseText, 'text/html');
        const expected = response.querySelector('#bc-applications');
        if (!expected || expected.querySelectorAll('tr').length < 2) return;
        const signature = tableSignature(expected);
        // Let the provider's readyState handler and synchronous formatting finish.
        queueMicrotask(() => {
          if (requestId !== latestGuideRequest || location.pathname !== path) return;
          const actual = document.querySelector('#Applications #bc-applications');
          if (actual && tableSignature(actual) === signature) markFitmentTableReady(actual);
        });
      } catch (_) { /* Invalid/empty responses never acknowledge readiness. */ }
    }, { once: true });
    return result;
  };

  document.addEventListener('input', event => {
    if (inputIds.includes(event.target?.id)) editedInputs.add(event.target);
  }, true);

  document.addEventListener('click', event => {
    if (!(event.target instanceof Element)) return;
    const option = event.target.closest('#SearchDropDownContainer .ddl-search td');
    const list = option?.closest('tbody[id$="list"]');
    if (!list) return;
    const input = document.getElementById('ddl' + list.id.replace(/list$/, ''));
    if (input) editedInputs.delete(input);
  }, true);

  // Back/forward cache can restore an older script instance after a newer search.
  window.addEventListener('pageshow', () => {
    try {
      const saved = JSON.parse(readPersistent(STORAGE_KEY) || 'null');
      current = saved?.version === 1 ? validate(saved.vehicle) : null;
    } catch (_) {}
    publish();
  });

  window.addEventListener('storage', event => {
    try { if (event.storageArea && event.storageArea !== localStorage) return; } catch (_) { return; }
    if (event.key === STORAGE_KEY || event.key === null) {
      try {
        const saved = JSON.parse(readPersistent(STORAGE_KEY) || 'null');
        current = saved?.version === 1 ? validate(saved.vehicle) : null;
      } catch (_) { current = null; }
      publish();
    }
    if (event.key === OPTION_CACHE_KEY || event.key === null) refreshOptionCache();
  });

  document.addEventListener('click', event => {
    if (!(event.target instanceof Element)) return;
    const garageAction = event.target.closest('.lnkgaragevehicle[onclick]');
    if (garageAction && !event.defaultPrevented && garageAction.getAttribute('aria-disabled') !== 'true') {
      // Parse the known handler's numeric index; never execute the attribute text.
      const match = /^\s*(?:return\s+)?SearchProductToGarage\(\s*['"]?(\d+)['"]?\s*\)\s*;?\s*$/.exec(
        garageAction.getAttribute('onclick') || ''
      );
      if (match) {
        const vehicle = readGarageVehicle(match[1]);
        if (vehicle) setVehicle(vehicle);
        else clearVehicle(); // A new unreadable selection must not confirm the old vehicle.
        return;
      }
    }
    const button = event.target.closest('#btnsearch, #btnclear, #btnVINSearch');
    if (!button || !button.closest('#Pc_rootdiv') || button.disabled ||
        button.getAttribute('aria-disabled') === 'true' || event.defaultPrevented) return;

    if (button.id === 'btnclear' || button.id === 'btnVINSearch') {
      // VIN decoding is not integrated yet. Never reuse an unrelated manual vehicle.
      clearVehicle();
      return;
    }
    const vehicle = readSubmittedVehicle();
    if (vehicle) setVehicle(vehicle);
    else clearVehicle(); // An invalid new attempt must not leave an old vehicle confirmed.
  }, true); // Save synchronously before PCFitment's inline navigation handler.

  // Visiting My Garage preserves the last submitted vehicle. Only its View
  // Products action (including the forwarded mobile action) updates the record.
  publish();
})();

